import * as React from 'react'
import { cn } from '../../lib/cn'
import { Tabs, TabsContent, TabsList, TabsTrigger } from './TabBar'

/**
 * MarkdocTabs — content-agnostic tabbed panel. The Markdoc
 * `{% tabs %}{% tab name="UI" %}…{% /tab %}{% tab name="API" %}…{% /tab %}{% /tabs %}`
 * tag compiles to this. Content per tab can be any React node — the
 * Markdoc compiler keeps the inner markup so callouts / lists / code-examples
 * inside a tab render through their respective renderers.
 *
 * For multi-language CODE specifically, prefer `MarkdocCodeExample` — it adds
 * the proper CodeBlock chrome (language tag + copy button) per tab.
 */
export interface MarkdocTabEntry {
  /** Tab key — used internally; surfaces as `data-state` value. */
  name: string
  /** Visible tab label. Defaults to `name`. */
  label?: string
  content: React.ReactNode
}

export interface MarkdocTabsProps extends React.HTMLAttributes<HTMLDivElement> {
  tabs: MarkdocTabEntry[]
  /** Default-selected tab `name`. Falls back to the first tab. */
  defaultName?: string
}

export const MarkdocTabs = React.forwardRef<HTMLDivElement, MarkdocTabsProps>(
  ({ className, tabs, defaultName, ...props }, ref) => {
    if (tabs.length === 0) return null
    const initial = tabs.find((t) => t.name === defaultName) ?? tabs[0]
    return (
      <div ref={ref} className={cn('flex flex-col', className)} {...props}>
        <Tabs defaultValue={initial.name}>
          <TabsList>
            {tabs.map((t) => (
              <TabsTrigger key={t.name} value={t.name}>
                {t.label ?? t.name}
              </TabsTrigger>
            ))}
          </TabsList>
          {tabs.map((t) => (
            <TabsContent key={t.name} value={t.name}>
              {t.content}
            </TabsContent>
          ))}
        </Tabs>
      </div>
    )
  },
)
MarkdocTabs.displayName = 'MarkdocTabs'
